import { buildPublicContentManifest, type PublicContentRoute } from "../../../scripts/public-content-manifest";
import { PUBLIC_SITE_ORIGIN, publicUrl } from "@/lib/seo";
import { products } from "@/lib/site-data";

export type SsrRouteSeo = {
  path: string;
  title: string;
  description: string;
  canonicalPath: string;
  noindex: boolean;
  notFound: boolean;
  jsonLd: Record<string, unknown>[];
};

const FALLBACK_DESCRIPTION =
  "DuraTarp supplies fixed-specification PVC coated, laminated, mesh and specialty tarpaulin materials for industrial and commercial applications.";

const CANONICAL_ALIASES: Record<string, string> = {
  "/manufacturing": "/factory",
  "/quality-control": "/quality",
};

function titleFor(route: PublicContentRoute): string {
  return route.title.includes("DuraTarp") ? route.title : `${route.title} | DuraTarp`;
}

function breadcrumbItems(path: string, title: string) {
  const segments = path.split("/").filter(Boolean);
  const items = [{ "@type": "ListItem", position: 1, name: "Home", item: PUBLIC_SITE_ORIGIN }];

  if (segments.length === 0) return items;

  const parentPath = products.some((product) => `/${product.slug}` === path)
    ? "/products"
    : segments.length > 1
      ? `/${segments[0]}`
      : "";
  if (parentPath) {
    const parent = buildPublicContentManifest().find((entry) => entry.path === parentPath);
    items.push({ "@type": "ListItem", position: 2, name: parent?.title ?? segments[0], item: publicUrl(parentPath) });
  }

  items.push({
    "@type": "ListItem",
    position: items.length + 1,
    name: title.replace(/ \| DuraTarp$/, ""),
    item: publicUrl(path),
  });
  return items;
}

function jsonLdFor(route: PublicContentRoute, canonicalPath: string, title: string): Record<string, unknown>[] {
  const pageType = route.group === "technical" && canonicalPath.startsWith("/technical-center/") ? "Article" : "WebPage";
  const page: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": pageType,
    name: title.replace(/ \| DuraTarp$/, ""),
    description: route.description,
    url: publicUrl(canonicalPath),
    isPartOf: { "@type": "WebSite", name: "DuraTarp", url: PUBLIC_SITE_ORIGIN },
  };

  if (pageType === "Article") {
    page.author = { "@type": "Organization", name: "DuraTarp", url: PUBLIC_SITE_ORIGIN };
    page.publisher = { "@type": "Organization", name: "DuraTarp", url: PUBLIC_SITE_ORIGIN };
    page.mainEntityOfPage = publicUrl(canonicalPath);
  }

  return [
    page,
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbItems(canonicalPath, title),
    },
  ];
}

export function resolveSsrRouteSeo(pathname: string, noindex = false): SsrRouteSeo {
  const cleanedPath = pathname === "/" ? "/" : pathname.replace(/\/+$/, "") || "/";
  const canonicalPath = CANONICAL_ALIASES[cleanedPath] ?? cleanedPath;
  const route = buildPublicContentManifest().find((entry) => entry.path === canonicalPath);

  if (!route) {
    return {
      path: cleanedPath,
      title: "Page not found | DuraTarp",
      description: FALLBACK_DESCRIPTION,
      canonicalPath: cleanedPath,
      noindex: true,
      notFound: true,
      jsonLd: [],
    };
  }

  const title = titleFor(route);
  return {
    path: cleanedPath,
    title,
    description: route.description || FALLBACK_DESCRIPTION,
    canonicalPath,
    noindex: noindex || canonicalPath !== cleanedPath,
    notFound: false,
    jsonLd: jsonLdFor(route, canonicalPath, title),
  };
}
